import React, { useEffect, useState } from 'react';
import { Download, Check } from 'lucide-react';

/**
 * InstallPWAButton — shows a button to install the BÉNI app on Chrome / Edge / Android.
 * - Listens to `beforeinstallprompt` and triggers the native install dialog on click.
 * - Hides itself if the app is already installed (display-mode: standalone) or on browsers
 *   that don't support the prompt (iOS Safari shows an instructions hint instead).
 *
 * Props:
 *   label?: string  — custom label (default: "Installer dans Chrome")
 *   className?: string — extra Tailwind classes for the button
 */
export const InstallPWAButton = ({ label, className = '' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    // Already running standalone?
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          setInstalled(true);
        }
      } catch (e) {
        console.warn('Install prompt error', e);
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    // No native prompt available (iOS Safari or already dismissed) — show a hint.
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    if (isIOS) {
      setShowIOSHint(true);
      setTimeout(() => setShowIOSHint(false), 6000);
    } else {
      alert(
        "Pour installer cette application :\n\n" +
        "• Chrome / Edge (ordinateur) : menu ⋮ → « Installer BÉNI… »\n" +
        "• Android : menu ⋮ → « Ajouter à l'écran d'accueil »\n" +
        "• Safari iOS : Partager → « Sur l'écran d'accueil »"
      );
    }
  };

  if (installed) {
    return (
      <div
        data-testid="pwa-installed-badge"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 ${className}`}
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
        App installée
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-pwa-button"
        title="Installer l'application BÉNI"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/40 hover:bg-[#d4af37] hover:text-black transition active:scale-95 ${className}`}
      >
        <Download className="w-3.5 h-3.5" />
        {label || 'Installer dans Chrome'}
      </button>
      {showIOSHint && (
        <div
          data-testid="pwa-ios-hint"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] max-w-sm bg-[#121212] border border-[#d4af37]/40 text-white text-xs px-4 py-3 rounded-lg shadow-2xl"
        >
          Sur iPhone : touchez l'icône <strong>Partager</strong> puis <strong>« Sur l'écran d'accueil »</strong>.
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
